
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/hooks/use-theme";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect } from "react";
import AuthGuard from "@/components/auth/AuthGuard";

const Settings = () => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  
  // Log current theme state for debugging
  useEffect(() => {
    console.log('Settings page - Current theme state:', { theme, resolvedTheme });
  }, [theme, resolvedTheme]);

  const handleDarkModeToggle = (checked: boolean) => {
    console.log('Dark mode toggle clicked, setting to:', checked ? 'dark' : 'light');
    const newTheme = checked ? 'dark' : 'light';
    setTheme(newTheme);
    
    // Apply the theme directly to the document for immediate feedback
    if (newTheme === "dark") {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleThemeSelect = (value: string) => {
    console.log('Theme select changed to:', value);
    if (value === 'light' || value === 'dark' || value === 'system') {
      setTheme(value as 'light' | 'dark' | 'system');
      
      // Apply the theme directly to the document for immediate feedback
      if (value === "dark") {
        document.documentElement.classList.add('dark');
      } else if (value === "light") {
        document.documentElement.classList.remove('dark');
      }
    }
  };

  return (
    <AuthGuard>
      <div className="container mx-auto py-4">
        <h1 className="text-3xl font-bold mb-6">Settings</h1>
        
        <Tabs defaultValue="appearance" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="appearance">Appearance</TabsTrigger>
            <TabsTrigger value="account">Account</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
          </TabsList>
          
          <TabsContent value="appearance">
            <Card>
              <CardHeader>
                <CardTitle>Appearance</CardTitle>
                <CardDescription>
                  Customize how the Travel CMS looks and feels.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="dark-mode">Dark Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      Toggle between light and dark theme
                    </p>
                  </div>
                  <Switch 
                    id="dark-mode" 
                    checked={theme === "dark"}
                    onCheckedChange={handleDarkModeToggle}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="theme-select">Theme Preference</Label>
                  <Select 
                    value={theme || "system"} 
                    onValueChange={handleThemeSelect}
                  >
                    <SelectTrigger className="w-full sm:w-[200px]" id="theme-select">
                      <SelectValue placeholder="Select a theme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Choose your preferred theme or use your system settings
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="account">
            <Card>
              <CardHeader>
                <CardTitle>Account</CardTitle>
                <CardDescription>
                  Manage your account settings and preferences.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p>Account settings will be implemented in a future update.</p>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notifications</CardTitle>
                <CardDescription>
                  Configure how you receive notifications.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p>Notification settings will be implemented in a future update.</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AuthGuard>
  );
};

export default Settings;
